import React, { useState } from 'react';
import {
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { User, AssignmentHistory } from '../../types';

interface MultiAssigneeSelectorProps {
  users: User[];
  selectedAssignees: string[];
  onAssigneesChange: (assigneeIds: string[]) => void;
  assignmentHistory?: AssignmentHistory[];
  currentUserId: string;
  issueKey?: string;
  apiAssigneesUsers?: any[];
  disabled?: boolean;
}

export const MultiAssigneeSelector: React.FC<MultiAssigneeSelectorProps> = ({
  users,
  selectedAssignees,
  onAssigneesChange,
  assignmentHistory = [],
  currentUserId,
  issueKey,
  apiAssigneesUsers = [],
  disabled = false
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  

  const availableUsers = users.filter(
    user => !selectedAssignees.includes(user.id)
  );
  
  // Use API assignees_users data if available, otherwise fall back to local users
  const assignedUsers = (apiAssigneesUsers.length > 0 
    ? apiAssigneesUsers.map(apiUser => ({
        id: apiUser.id,
        displayName: apiUser.display_name,
        emailAddress: apiUser.email_address,
        avatarUrl: apiUser.avatar_url || undefined, // "" -> undefined
        active: apiUser.active,
        groups: apiUser.groups
      }))
    : users.filter(user => selectedAssignees.includes(user.id))
  ).filter(u => u.active && users.some(pu => pu.id === u.id));

  const handleAddAssignee = (userId: string) => {
    if (disabled) return;
    if (!selectedAssignees.includes(userId)) {
      onAssigneesChange([...selectedAssignees, userId]);
    }
    setIsDropdownOpen(false);
  };

  const handleRemoveAssignee = (userId: string) => {
    if (disabled) return;
    onAssigneesChange(selectedAssignees.filter(id => id !== userId));
  };

  const formatHistoryTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return `${Math.floor(diffInHours / 24)}d ago`;
    }
  };

  // Generate a readable color from any string
  const stringToColor = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 70%, 45%)`;
  };

  // Get the first initial from displayName or email
  const getInitial = (name?: string, email?: string) => {
    const s = (name || email || "").trim();
    // Pick first alphanumeric character (handles spaces/symbols)
    const match = s.match(/\p{L}|\p{N}/u);
    return match ? match[0].toUpperCase() : "?";
  };

  const Avatar: React.FC<{ src?: string; name?: string; email?: string; className?: string }> = ({
    src,
    name,
    email,
    className = "",
  }) => {
    const [imgError, setImgError] = React.useState(false);
    const label = name || email || "";
    const bg = stringToColor(label);
  
    if (src && !imgError) {
      return (
        <img
          src={src}
          alt={label}
          onError={() => setImgError(true)}
          className={`w-8 h-8 rounded-full border-2 border-white shadow-sm ${className}`}
        />
      );
    }
  
    return (
      <div
        className={`w-8 h-8 rounded-full border-2 border-white shadow-sm flex items-center justify-center text-white text-xs font-semibold ${className}`}
        style={{ backgroundColor: bg }}
        aria-label={label}
        title={label}
      >
        {getInitial(name, email)}
      </div>
    );
  };

  
  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Assignees ({selectedAssignees.length})
          </label>
          {assignmentHistory.length > 0 && (
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700"
              type="button"
            >
              <HistoryIcon style={{ fontSize: 14 }} />
              <span>History</span>
            </button>
          )}
        </div>

        {/* Selected Assignees */}
        <div className="space-y-2 mb-3">
          {assignedUsers.map(user => {
            const name = (user.displayName);
            return (
              <div key={user.id} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center space-x-3">
                    <Avatar
                      src={user.avatarUrl}
                      name={user.displayName}
                      email={user.emailAddress}
                    />
                  <div>
                    <div className="font-medium text-gray-900 text-sm">{user.displayName}</div>
                    <div className="text-xs text-gray-500">{user.emailAddress}</div>
                  </div>
                </div>
                {!disabled && (
                  <button
                    onClick={() => handleRemoveAssignee(user.id)}
                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    type="button"
                  >
                    <CloseIcon style={{ fontSize: 16 }} />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {selectedAssignees.length === 0 && (
          <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <PersonIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No assignees selected</p>
          </div>
        )}
      </div>

      {/* Add Assignee Dropdown */}
      <div className="relative">
        <button
          onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
          className="w-full flex items-center justify-center space-x-2 p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50"
          type="button"
          disabled={disabled}
        >
          <PersonAddIcon style={{ fontSize: 18 }} />
          <span className="text-sm font-medium">Add Assignee</span>
        </button>

        {isDropdownOpen && !disabled && availableUsers.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
            {availableUsers.map(user => (
              <button
                key={user.id}
                onClick={() => handleAddAssignee(user.id)}
                className="w-full flex items-center space-x-3 p-3 hover:bg-gray-50 transition-colors text-left"
                type="button"
              >
                <Avatar
                  src={user.avatarUrl}
                  name={user.displayName}
                  email={user.emailAddress}
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 text-sm">{user.displayName}</div>
                  <div className="text-xs text-gray-500">{user.emailAddress}</div>
                </div>
                <CheckIcon className="text-green-600" style={{ fontSize: 16 }} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Assignment History */}
      {showHistory && assignmentHistory.length > 0 && (
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Assignment History</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {assignmentHistory.map(history => {
              const user = users.find(u => u.id === history.userId);
              const assignedBy = users.find(u => u.id === history.assignedBy);

              return (
                <div key={history.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    history.action === 'assigned' ? 'bg-green-500' : 'bg-red-500'
                  }`}></div>
                  <div className="flex-1">
                    <div className="text-sm text-gray-900">
                      <span className="font-medium">{user?.displayName}</span>
                      <span className={history.action === 'assigned' ? 'text-green-600' : 'text-red-600'}>
                        {' '}{history.action === 'assigned' ? 'assigned' : 'unassigned'}
                      </span>
                      {' '}by <span className="font-medium">{assignedBy?.displayName}</span>
                    </div>
                    {history.reason && (
                      <div className="text-xs text-gray-600 mt-1">{history.reason}</div>
                    )}
                    <div className="text-xs text-gray-500 mt-1">
                      {formatHistoryTime(history.timestamp)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>

  );
};